import jsPDF from "jspdf";
import qrcode from "qrcode-generator";

export const CONTACT_PT_TO_MM = 0.3527;
export const CONTACT_FIELDS = [
  { key: "name", label: "Nombre del asesor", placeholder: "Tu nombre", maxLength: 100, x: 100, y: 107, width: 174, height: 23, fontPt: 26, minFontPt: 16, maxLines: 2, bold: true },
  { key: "title", label: "Cargo del asesor", placeholder: "Cargo o especialidad", maxLength: 100, x: 100, y: 133, width: 174, height: 12, fontPt: 16, minFontPt: 11, maxLines: 2 },
  { key: "company", label: "Empresa", placeholder: "Nombre de la empresa", maxLength: 100, x: 100, y: 150, width: 174, height: 7, fontPt: 12, minFontPt: 9, maxLines: 1 },
  { key: "phone", label: "WhatsApp o teléfono", placeholder: "Tu teléfono", maxLength: 60, x: 44, y: 188, width: 100, height: 24, fontPt: 18, minFontPt: 11, maxLines: 3 },
  { key: "email", label: "Correo electrónico", placeholder: "Tu correo", maxLength: 160, x: 176, y: 188, width: 100, height: 24, fontPt: 18, minFontPt: 10, maxLines: 3 },
  { key: "address", label: "Dirección de la oficina", placeholder: "Dirección de tu oficina", maxLength: 240, x: 44, y: 243, width: 100, height: 24, fontPt: 18, minFontPt: 10, maxLines: 3 },
];

export function resolveReportContact(branding = {}, overrides = {}) {
  const brand = branding || {};
  const agent = brand.agent || {};
  const defaults = {
    name: agent.name || "",
    title: agent.job_title || "Asesor inmobiliario",
    company: brand.name || "",
    phone: agent.phone || brand.phone || "",
    email: agent.email || brand.email || "",
    address: agent.office_address || brand.address || "",
    photoUrl: agent.photo_url || "",
  };
  return Object.fromEntries(Object.entries(defaults).map(([key, value]) =>
    [key, String(overrides[key] ?? value)]));
}

const brandColor = (value) => {
  if (/^#[a-f\d]{6}$/i.test(value || "")) return value;
  if (/^#[a-f\d]{3}$/i.test(value || "")) return "#" + value.slice(1).split("").map(c => c + c).join("");
  return "#FA7317";
};
const tint = (color) => "#" + [1, 3, 5].map(index =>
  Math.round(parseInt(color.slice(index, index + 2), 16) * 0.06 + 255 * 0.94).toString(16).padStart(2, "0")).join("");

let metrics;
function measure(value, fontPt, bold) {
  metrics ||= new jsPDF({ unit: "mm" });
  metrics.setFont("helvetica", bold ? "bold" : "normal");
  metrics.setFontSize(fontPt);
  return metrics.getTextWidth(value);
}

function wrapText(value, width, fontPt, bold) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const word of words) {
    if (line && measure(line + " " + word, fontPt, bold) <= width) {
      line += " " + word;
      continue;
    }
    if (line) lines.push(line);
    line = "";
    // También admite direcciones de correo y nombres sin espacios.
    for (const character of word) {
      if (line && measure(line + character, fontPt, bold) > width) {
        lines.push(line);
        line = "";
      }
      line += character;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function layoutField(field, value) {
  let fontPt = field.fontPt;
  let lines = [];
  for (; fontPt >= 8; fontPt--) {
    lines = wrapText(value, field.width, fontPt, field.bold);
    if ((lines.length <= field.maxLines || fontPt < field.minFontPt) && lines.length * fontPt * CONTACT_PT_TO_MM * 1.2 <= field.height) break;
  }
  fontPt = Math.max(fontPt, 8);
  const lineHeight = fontPt * CONTACT_PT_TO_MM * 1.2;
  return { ...field, value, lines, fontPt, lineHeight, baseline: field.y + fontPt * CONTACT_PT_TO_MM * 0.82 };
}

export function contactLink(key, rawValue) {
  const value = String(rawValue || "").trim();
  if (key === "phone") {
    const digits = value.replace(/\D/g, "");
    return /^\d{7,15}$/.test(digits) ? "https://wa.me/" + digits : null;
  }
  if (key === "email" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "mailto:" + encodeURIComponent(value);
  if (key === "address" && value) return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(value);
  return null;
}

let lastLocationQr;
function locationQr(url) {
  if (lastLocationQr?.url === url) return lastLocationQr;
  const code = qrcode(0, "M");
  code.addData(url);
  code.make();
  const count = code.getModuleCount();
  const runs = [];
  // Agrupamos módulos contiguos para dibujarlos como vectores en la vista y el PDF.
  for (let row = 0; row < count; row++) {
    let column = 0;
    while (column < count) {
      if (!code.isDark(row, column)) { column++; continue; }
      const start = column;
      while (column < count && code.isDark(row, column)) column++;
      runs.push({ row, column: start, width: column - start });
    }
  }
  lastLocationQr = { url, count, runs };
  return lastLocationQr;
}

// Símbolos tipo emoji, vectoriales para que se impriman sin depender de una fuente de emojis.
function addIcon(elements, kind, x, y) {
  const colors = { phone: ["#eaf8f0", "#16865b"], email: ["#edf4ff", "#3b6fb6"], address: ["#fff0e6", "#d66b24"] };
  const [fill, stroke] = colors[kind];
  elements.push({ type: "rect", x, y, width: 10, height: 10, radius: 3, fill });
  const path = (commands, filled = false) => elements.push({ type: "path", x: x + 2, y: y + 2, scale: 0.25, commands, stroke, fill: filled ? stroke : null, strokeWidth: 0.45 });
  if (kind === "phone") {
    path([["m", 4, 3], ["l", 8, 2], ["l", 11, 8], ["l", 8, 10], ["c", 9, 13, 11, 15, 14, 16], ["l", 16, 13], ["l", 22, 16], ["l", 21, 20], ["c", 20, 23, 12, 20, 8, 16], ["c", 4, 12, 1, 5, 4, 3], ["h"]], true);
  } else if (kind === "email") {
    path([["m", 2, 4], ["l", 22, 4], ["l", 22, 20], ["l", 2, 20], ["h"], ["m", 2, 5], ["l", 12, 13], ["l", 22, 5]]);
  } else {
    path([["m", 12, 23], ["c", 9, 19, 3, 13, 3, 9], ["c", 3, -2, 21, -2, 21, 9], ["c", 21, 13, 15, 19, 12, 23], ["h"]], true);
    elements.push({ type: "circle", x: x + 5, y: y + 4.1, radius: 0.9, fill: "#ffffff" });
  }
}

export function buildContactReportScene(contact, color = "#FA7317", showEmptyFields = false) {
  const accent = brandColor(color);
  const soft = tint(accent);
  const elements = [];
  const rect = (x, y, width, height, radius, fill, stroke) => elements.push({ type: "rect", x, y, width, height, radius, fill, stroke });
  const text = (value, x, y, fontPt, textColor = "#64748b", bold = false) =>
    elements.push({ type: "text", value, x, y, fontPt, color: textColor, bold });
  rect(0, 0, 320, 297, 0, "#ffffff");
  rect(0, 0, 320, 3, 0, accent);
  rect(0, 294, 320, 3, 0, accent);
  elements.push({ type: "image", key: "logo", x: 36, y: 23, width: 70, height: 25, radius: 0 });
  text("ESTAMOS PARA ACOMPAÑARTE", 213, 39, 10, "#64748b", true);
  text("Sigamos en contacto", 34, 69, 30, "#1e293b", true);
  text("Conversemos sobre tu propiedad y los próximos pasos.", 34, 82, 14);

  rect(34, 98, 252, 64, 7, "#f1f5f9");
  rect(34, 97, 252, 64, 7, soft, "#e2e8f0");
  rect(42, 107, 44, 44, 5, "#ffffff", "#e2e8f0");
  elements.push({ type: "image", key: "avatar", x: 42, y: 107, width: 44, height: 44, radius: 5 });
  const rows = [
    { key: "phone", label: "WHATSAPP / TELÉFONO", x: 34, y: 170, width: 120 },
    { key: "email", label: "CORREO ELECTRÓNICO", x: 166, y: 170, width: 120 },
    { key: "address", label: "DIRECCIÓN", x: 34, y: 225, width: 120 },
  ];
  rows.forEach(row => {
    if (!String(contact[row.key] || "").trim() && !showEmptyFields) return;
    rect(row.x, row.y, row.width, 45, 5, "#ffffff", "#e2e8f0");
    addIcon(elements, row.key, row.x + 8, row.y + 6);
    text(row.label, row.x + 23, row.y + 12.4, 10, "#64748b", true);
  });
  const locationUrl = contactLink("address", contact.address);
  const qrLink = locationUrl ? { x: 166, y: 225, width: 120, height: 45, url: locationUrl } : null;
  if (qrLink) {
    const qr = locationQr(locationUrl);
    const quietZone = 4;
    const moduleSize = 35 / (qr.count + quietZone * 2);
    rect(qrLink.x, qrLink.y, qrLink.width, qrLink.height, 5, "#ffffff", "#e2e8f0");
    rect(171, 230, 35, 35, 0, "#ffffff");
    qr.runs.forEach(run => rect(
      171 + (run.column + quietZone) * moduleSize,
      230 + (run.row + quietZone) * moduleSize,
      run.width * moduleSize, moduleSize, 0, "#1e293b",
    ));
    text("Cómo llegar", 212, 239, 14, "#1e293b", true);
    text("Escaneá el código", 212, 249, 11);
    text("y abrí la ubicación.", 212, 256, 11);
  }
  const fields = CONTACT_FIELDS.map(field => {
    const result = layoutField(field, contact[field.key] || "");
    result.lines.forEach((line, index) =>
      text(line, result.x, result.baseline + result.lineHeight * index, result.fontPt, field.key === "name" ? "#1e293b" : "#475569", field.bold));
    return result;
  });
  text("Gracias por confiar en este análisis.", 34, 282, 11);
  return { elements, fields, qrLink, profileFill: soft };
}

export function imagePlacement(image, box) {
  const padding = image.fallback && box.key === "avatar" ? 5 : 0;
  const width = box.width - padding * 2;
  const height = box.height - padding * 2;
  const ratio = image.fit === "cover"
    ? Math.max(width / image.dims.w, height / image.dims.h)
    : Math.min(width / image.dims.w, height / image.dims.h);
  const drawWidth = image.dims.w * ratio;
  const drawHeight = image.dims.h * ratio;
  return { x: box.x + (box.width - drawWidth) / 2, y: box.y + (box.height - drawHeight) / 2, width: drawWidth, height: drawHeight };
}

export function drawContactReport(doc, contact, color, images) {
  const { elements, fields, qrLink } = buildContactReportScene(contact, color);
  doc.saveGraphicsState();
  elements.forEach(element => {
    if (element.type === "text") {
      doc.setFont("helvetica", element.bold ? "bold" : "normal");
      doc.setFontSize(element.fontPt);
      doc.setTextColor(element.color);
      doc.text(element.value, element.x, element.y);
    } else if (element.type === "image") {
      const image = images[element.key];
      if (!image?.data || !image.dims) return;
      const placement = imagePlacement(image, element);
      doc.saveGraphicsState();
      doc.roundedRect(element.x, element.y, element.width, element.height, element.radius, element.radius, null);
      doc.clip();
      doc.discardPath();
      doc.addImage(image.data, image.format, placement.x, placement.y, placement.width, placement.height);
      doc.restoreGraphicsState();
    } else if (element.type === "path") {
      doc.setDrawColor(element.stroke);
      doc.setLineWidth(element.strokeWidth);
      if (element.fill) doc.setFillColor(element.fill);
      const commands = element.commands.map(([op, ...points]) => ({
        op,
        c: points.map((point, index) => point * element.scale + (index % 2 ? element.y : element.x)),
      }));
      doc.path(commands);
      if (element.fill) doc.fill(); else doc.stroke();
    } else {
      doc.setFillColor(element.fill);
      if (element.type === "circle") {
        doc.circle(element.x, element.y, element.radius, "F");
      } else {
        if (element.stroke) {
          doc.setDrawColor(element.stroke);
          doc.setLineWidth(0.3);
        }
        const style = element.stroke ? "FD" : "F";
        if (element.radius) doc.roundedRect(element.x, element.y, element.width, element.height, element.radius, element.radius, style);
        else doc.rect(element.x, element.y, element.width, element.height, style);
      }
    }
  });
  fields.forEach(field => {
    const url = contactLink(field.key, field.value);
    if (url) doc.link(field.x, field.y, field.width, field.height, { url });
  });
  if (qrLink) doc.link(qrLink.x, qrLink.y, qrLink.width, qrLink.height, { url: qrLink.url });
  doc.restoreGraphicsState();
}
